import { ObjectConfig, useFormState } from "@homebound/form-state";
import { useEffect, useState } from "react";
import { BoundNumberField, Checkbox, Modal, Switch, TransparentHBLoadingSpinnerOnly } from "~components";
import { Css } from "~generated/css";
import { SaveCheckoutConfigInput } from "~generated/graphqlTypes";
import { safeEntries, safeKeys, useComputed } from "~utils";
import {
  PdfPrintModal_CheckoutConfigFragment,
  usePrintModal_SaveAdditionalSitePriceMutation,
} from "./PDFPrintModal.generated";
import { PrintSummaryReports, PrintSummaryReportSlugs } from "./SummaryPrint.page";
import { useSummaryPrintConfigurationQuery } from "./SummaryPrint.page.generated";

export type PDFPrintModalProps = {
  onClose: () => void;
  onContinue: (exports: string[], includePricing: boolean, additionalSitePriceInCents?: number) => void;
  // The checkout config to use for the modal. This is used to populate the form state and the mutation.
  checkoutConfig: PdfPrintModal_CheckoutConfigFragment;
  // Hide the ADU checkbox. Prevent confusion from the user seeing an ADU when they haven't selected anything ADU related
  allowAduReport: boolean;
};

export function PDFPrintModal(props: PDFPrintModalProps) {
  const { onClose, onContinue, checkoutConfig, allowAduReport } = props;

  // default state for all of these is falsey so we don't need keys or values here
  const [checkValues, setCheckValues] = useState<{ [key in PrintSummaryReportSlugs]?: boolean }>({ summary: true });
  const [includePricing, setIncludePricing] = useState(false);
  const [includeInvoiceSchedule, setIncludeInvoiceSchedule] = useState(false);

  const isInteriorSelected = checkValues.interior ?? false;

  // Query to check if InsideMaps room renderings are ready (only when Interior is selected)
  const query = useSummaryPrintConfigurationQuery({
    variables: { checkoutConfigId: checkoutConfig.id },
    skip: !isInteriorSelected,
  });

  const hasInteriorImages = (query.data?.checkoutConfig?.insideMapsRoomRenderings ?? []).nonEmpty;
  const shouldPoll = isInteriorSelected && !hasInteriorImages;

  /** InsideMaps images may be processing on the backend. Detect if that's happening, and start polling until images are ready. */
  useEffect(() => {
    shouldPoll ? query.startPolling(2_500) : query.stopPolling();
  }, [query, shouldPoll]);

  const [savePrice] = usePrintModal_SaveAdditionalSitePriceMutation();
  const formState = useFormState({
    config: additionalSitePriceFormConfig,
    init: {
      input: checkoutConfig,
      map: (cc) => ({ id: cc.id, additionalSitePriceInCents: cc.additionalSitePriceInCents ?? 0 }),
    },
    autoSave: (fs) => savePrice({ variables: { input: fs.value } }),
  });

  const additionalSitePriceInCents = useComputed(() => formState.additionalSitePriceInCents.value ?? 0, [formState]);

  return (
    <Modal
      size={{ width: "696px", height: "498" }}
      title={
        <div css={Css.df.jcfs.gap2.pb4.$}>
          <span css={Css.t24.$}>Select what to include in PDF export</span>
        </div>
      }
      description={
        <div css={Css.t18.fw300.wPx(568).$}>
          {safeEntries(PrintSummaryReports)
            .filter(([key]) => key !== "estimatedInvoiceSchedule")
            // customerSummary is driven by the dedicated "Homeowner PDF" button, not this internal checkbox list
            .filter(([key]) => key !== "customerSummary")
            .filter(([key]) => key !== "adu" || allowAduReport)
            .map(([key, { label }]) => {
              return (
                <div css={Css.df.mb2.$} key={key}>
                  <Checkbox
                    selected={checkValues[key] ?? false}
                    onClick={() => setCheckValues({ ...checkValues, [key]: !checkValues[key] })}
                  />
                  <p css={Css.t14.gray700.ml2.lhPx(22).$}>{label}</p>
                </div>
              );
            })}
          <div css={Css.df.mb2.$}>
            <Switch value={includePricing} onClick={() => setIncludePricing(!includePricing)} />
            <p css={Css.t14.gray700.ml2.$}>Include pricing</p>
          </div>
          <div css={Css.df.mb2.aic.$}>
            <Checkbox
              data-testid="invoiceScheduleToggle"
              selected={includeInvoiceSchedule}
              onClick={() => setIncludeInvoiceSchedule(!includeInvoiceSchedule)}
            />
            <p css={Css.t14.gray700.ml2.$}>Estimated Invoice Schedule</p>
          </div>
          {includeInvoiceSchedule && (
            <div css={Css.ml4.mb2.$}>
              <BoundNumberField
                label="Additional Site Price"
                type="cents"
                field={formState.additionalSitePriceInCents}
                placeholder="0.00"
              />
              <p css={Css.t12.gray700.mtPx(4).$}>
                This amount is added to the home's price and split across the Foundation (80%) and Exterior (20%)
                invoice categories. Saved automatically as you edit.
              </p>
            </div>
          )}
        </div>
      }
      actionBtns={[
        { variant: "link", children: "Cancel", onClick: onClose },
        {
          children: (
            <div css={Css.df.aic.gap1.$}>
              {shouldPoll && (
                <span css={Css.df.aic.jcc.wPx(20).hPx(20).$}>
                  <TransparentHBLoadingSpinnerOnly />
                </span>
              )}
              <span>{shouldPoll ? "Rendering" : "Export"}</span>
            </div>
          ),
          disabled: shouldPoll
            ? "Preparing interior images for export..."
            : Object.values(checkValues).every((value) => !value) && !includeInvoiceSchedule,
          onClick: () => {
            const exports = safeKeys(checkValues).reduce((acc: string[], current) => {
              if (checkValues[current]) {
                acc.push(current);
              }
              return acc;
            }, []);

            if (includeInvoiceSchedule) exports.push("estimatedInvoiceSchedule");

            onContinue(exports, includePricing, additionalSitePriceInCents);
            onClose();
          },
        },
      ]}
      isOpen
      close={onClose}
    ></Modal>
  );
}

type AdditionalSitePriceFormValue = Pick<SaveCheckoutConfigInput, "id" | "additionalSitePriceInCents">;
const additionalSitePriceFormConfig: ObjectConfig<AdditionalSitePriceFormValue> = {
  id: { type: "value" },
  additionalSitePriceInCents: { type: "value" },
};
